import * as React from "react";
import {
  ArrayField,
  BooleanField,
  DataTable,
  DateField,
  FunctionField,
  Labeled,
  ListContextProvider,
  NumberField,
  Pagination,
  SelectField,
  useList,
  useRecordContext,
} from "react-admin";
import { Alert, Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { STOCK_TYPES } from "../enums/StockTypes";
import { STOCK_AVAILABILITY_MODES } from "../enums/StockAvailabilityModes";
import {
  STOCK_TYPES_OPTIONS,
  STOCK_AVAILABILITY_MODES_OPTIONS,
} from "../utils/ProductUtils";
import {
  formatTimeTimezone,
  formatUtcDateInput,
  todayDateKey,
} from "../utils/DateTimeUtils";
import { formatNumberToPercentage, hasValue } from "../utils/NumberUtils";

const DAYS_PER_PAGE = 50;

const EMPTY_TEXT = "-";

// DataTable.NumberCol drops `emptyText` on the way to its NumberField — it
// lands on the <td> instead — so empty cells are rendered explicitly here.
const orDash = (value) => (hasValue(value) ? value : EMPTY_TEXT);

// Stored at UTC midnight, so every date is rendered there too: the browser
// timezone would show the previous day.
const UTC_DATE_OPTIONS = { timeZone: "UTC" };

/**
 * `stockAvailability.days` is the configuration and `availableDays` is what the
 * API reports as still on sale — two views of the same calendar. Merging them
 * puts a day's target and its counter on one row, instead of leaving the reader
 * to pair up two tables by eye.
 *
 * Merged by date key and not by index on purpose: the lists can diverge, e.g. a
 * day just added to the config that the API has not snapshotted yet, or a past
 * day already dropped from the config but still counted.
 */
const mergeStockDays = (days, availableDays) => {
  const rowByDate = new Map();

  const rowFor = (date) => {
    const id = formatUtcDateInput(date);
    if (!id) return null;

    if (!rowByDate.has(id)) rowByDate.set(id, { id, date });

    return rowByDate.get(id);
  };

  for (const day of days ?? []) {
    const row = rowFor(day?.date);
    if (!row) continue;

    row.quantity = day.quantity;
    row.customMorfySplit = day.customMorfySplit;
  }

  for (const availableDay of availableDays ?? []) {
    const row = rowFor(availableDay?.date);
    if (!row) continue;

    row.initialStock = availableDay.initialStock;
    row.stock = availableDay.stock;
  }

  return [...rowByDate.values()].sort((a, b) => a.id.localeCompare(b.id));
};

// The API's own baseline minus what is left. Computed from initialStock rather
// than from the configured quantity: editing the config after the day started
// selling would otherwise report units that were never on sale.
const getSoldUnits = (row) =>
  hasValue(row?.initialStock) && hasValue(row?.stock)
    ? Number(row.initialStock) - Number(row.stock)
    : null;

const sumBy = (rows, getValue) =>
  rows.reduce((total, row) => {
    const value = getValue(row);
    return hasValue(value) ? total + Number(value) : total;
  }, 0);

const Stat = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
  </Box>
);

const StockDaysSummary = ({ rows }) => {
  const configured = sumBy(rows, (row) => row.quantity);
  const sold = sumBy(rows, getSoldUnits);
  const available = sumBy(rows, (row) => row.stock);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, width: "fit-content" }}>
      <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
        <Stat label="Días" value={rows.length} />
        <Stat label="Stock total" value={configured} />
        <Stat label="Vendido" value={sold} />
        <Stat label="Disponible" value={available} />
      </Stack>
    </Paper>
  );
};

const DateCell = ({ row, today }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <DateField record={row} source="date" options={UTC_DATE_OPTIONS} />
    {row.id === today && <Chip size="small" color="primary" label="Hoy" />}
  </Stack>
);

const AvailableCell = ({ row }) => {
  if (!hasValue(row.stock)) return EMPTY_TEXT;

  if (Number(row.stock) === 0) {
    return <Chip size="small" color="error" label="Agotado" />;
  }

  return row.stock;
};

const StockDaysTable = ({ rows }) => {
  const today = todayDateKey();
  const listContext = useList({
    data: rows,
    perPage: DAYS_PER_PAGE,
    sort: { field: "id", order: "ASC" },
  });

  // Only worth two columns of dashes when at least one day overrides the split.
  const hasCustomSplit = rows.some((row) =>
    hasValue(row.customMorfySplit?.percentage)
  );

  return (
    <ListContextProvider value={listContext}>
      <DataTable
        storeKey="product.stockDays"
        rowClick={false}
        bulkActionButtons={false}
        // Today stands out; past days step back so the eye lands on what is
        // still on sale.
        rowSx={(row) => ({
          ...(row.id === today && { bgcolor: "action.selected" }),
          ...(row.id < today && { opacity: 0.7 }),
        })}
      >
        <DataTable.Col
          source="id"
          label="Fecha"
          disableSort
          render={(row) => <DateCell row={row} today={today} />}
        />
        <DataTable.Col
          source="quantity"
          label="Stock inicial"
          align="right"
          disableSort
          render={(row) => orDash(row.quantity)}
        />
        <DataTable.Col
          label="Vendido"
          align="right"
          disableSort
          render={(row) => orDash(getSoldUnits(row))}
        />
        <DataTable.Col
          source="stock"
          label="Disponible"
          align="right"
          disableSort
          render={(row) => <AvailableCell row={row} />}
        />
        {hasCustomSplit && (
          <DataTable.Col
            source="customMorfySplit.quantity"
            label="Primeras unidades"
            align="right"
            disableSort
            render={(row) => orDash(row.customMorfySplit?.quantity)}
          />
        )}
        {hasCustomSplit && (
          <DataTable.Col
            source="customMorfySplit.percentage"
            label="Comisión"
            align="right"
            disableSort
            render={(row) =>
              hasValue(row.customMorfySplit?.percentage)
                ? formatNumberToPercentage(row.customMorfySplit.percentage)
                : EMPTY_TEXT
            }
          />
        )}
      </DataTable>
      {rows.length > DAYS_PER_PAGE && (
        <Pagination rowsPerPageOptions={[25, 50, 100]} />
      )}
    </ListContextProvider>
  );
};

const PerDayStockShow = ({ record }) => {
  const rows = mergeStockDays(
    record?.stockAvailability?.days,
    record?.availableDays
  );

  if (!rows.length) {
    return (
      <Alert severity="info">
        Este producto no tiene días de stock cargados.
      </Alert>
    );
  }

  return (
    <Box>
      <StockDaysSummary rows={rows} />
      <StockDaysTable rows={rows} />
    </Box>
  );
};

const StockOverridesTable = ({ country }) => (
  <ArrayField source="stockAvailability.overrides">
    <DataTable
      storeKey="product.stockAvailability.overrides"
      rowClick={false}
      bulkActionButtons={false}
      empty={
        <Alert severity="info">
          No hay ajustes de stock para fechas puntuales.
        </Alert>
      }
    >
      <DataTable.Col source="date" label="Fecha">
        <DateField source="date" />
      </DataTable.Col>
      <DataTable.NumberCol label="Stock" source="quantity" />
      <DataTable.Col source="restartHour" label="Horario de inicio">
        <FunctionField
          source="restartHour"
          render={(override) =>
            override?.restartHour
              ? formatTimeTimezone(country)(override.restartHour)
              : "-"
          }
        />
      </DataTable.Col>
    </DataTable>
  </ArrayField>
);

const RenewingStockShow = ({ record }) => {
  // Absent on a product whose restaurant was not embedded in the response;
  // formatTimeTimezone falls back to UTC rather than throwing.
  const country = record?.restaurantData?.country;

  return (
    <Stack spacing={1} alignItems="flex-start">
      <Labeled label="Stock desde">
        <DateField source="stockAvailability.dateRange.from" emptyText="-" />
      </Labeled>
      <Labeled label="Stock hasta">
        <DateField source="stockAvailability.dateRange.to" emptyText="-" />
      </Labeled>
      <Labeled label="Renovar stock diario">
        <BooleanField source="stockAvailability.renewDaily" emptyText="-" />
      </Labeled>
      <Labeled label="Inicio de stock diario">
        <FunctionField
          source="stockAvailability.dailyDefaults.restartHour"
          render={(product) =>
            product?.stockAvailability?.dailyDefaults?.restartHour
              ? formatTimeTimezone(country)(
                  product.stockAvailability.dailyDefaults.restartHour
                )
              : "-"
          }
        />
      </Labeled>
      <Labeled label="Stock diario por defecto">
        <NumberField
          source="stockAvailability.dailyDefaults.quantity"
          emptyText="-"
        />
      </Labeled>
      <Labeled label="Stock disponible">
        <NumberField source="stock" emptyText="-" />
      </Labeled>
      {record?.stockAvailability?.renewDaily && (
        <Labeled label="Ajuste de Stock" sx={{ width: "100%" }}>
          <StockOverridesTable country={country} />
        </Labeled>
      )}
    </Stack>
  );
};

/**
 * The Stock tab body of ProductShow, mirroring ProductStockTab on the edit side.
 *
 * The scalar `stock` is deliberately not shown in the per-day mode: it is a
 * single counter that does not track the sum of the day rows, so next to a
 * per-day table it reads as a contradiction. The totals above the table are
 * derived from the same rows the table shows.
 */
const ProductStockShowTab = () => {
  const record = useRecordContext();

  const isLimited = record?.stockType === STOCK_TYPES.LIMITED;
  const isPerDay =
    record?.stockAvailability?.mode === STOCK_AVAILABILITY_MODES.PER_DAY;

  return (
    <Stack spacing={2} alignItems="flex-start" sx={{ width: "100%" }}>
      <Labeled label="Tipo">
        <SelectField source="stockType" choices={STOCK_TYPES_OPTIONS} />
      </Labeled>

      {isLimited && (
        <>
          <Labeled label="Modo de stock">
            <SelectField
              source="stockAvailability.mode"
              choices={STOCK_AVAILABILITY_MODES_OPTIONS}
              // An absent mode is the legacy renewing one.
              emptyText="Renovable"
            />
          </Labeled>

          <Box sx={{ width: "100%" }}>
            {isPerDay ? (
              <PerDayStockShow record={record} />
            ) : (
              <RenewingStockShow record={record} />
            )}
          </Box>

          <Labeled label="Máximo por usuario por día">
            <NumberField source="maxUserStock" emptyText="-" />
          </Labeled>
        </>
      )}
    </Stack>
  );
};

export default ProductStockShowTab;
